import type { OwnRating as OwnRatingValue, TastingScale } from "@corklog/shared";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { setOwnRating } from "../api.js";
import { useMayWrite } from "../cellar.js";
import { actionButton, control, resting } from "./controls.js";

/**
 * What you think of this wine.
 *
 * The third kind of score, and the one the model was missing until seven real
 * wines were registered at 3.10: the same 17 was typed onto three identical
 * bottles of one rosé, because the judgement was about the wine and the only
 * box was on the bottle. It sits under the drinking window because those two
 * are the pair a decision is made from — is it ready, and is it any good.
 *
 * The reading carries its date. What one thinks of a wine changes as the wine
 * ages, so saying so again next year adds a reading rather than overwriting
 * the one before; this shows the latest, and the date is what says how old the
 * opinion is.
 */
export function OwnRating({
  cellarId,
  wineId,
  scale,
  current,
  onSaved,
}: {
  cellarId: string;
  wineId: string;
  /** Null until the cellar's scale has arrived. A number with no scale is the
   *  thing this must never show. */
  scale: TastingScale | null;
  current: OwnRatingValue | null;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [score, setScore] = useState(current === null ? "" : String(current.score));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mayWrite = useMayWrite();

  if (scale === null) return null;

  // A guest reads the cellar's own reading and cannot take another one. The
  // number is not price data and there is no reason to hide it — what changes
  // is the voice: *mitt betyg* is the owner's word for it, and a friend
  // reading the page is told whose judgement this is.
  if (mayWrite === false) {
    if (current === null) return null;
    return (
      <p className="text-muted mt-4 text-sm">
        {t("wine.ownersRating", { max: scale.max })}{" "}
        <span className="font-display text-ink text-lg">{current.score}</span>{" "}
        {t("wine.ratedOn", { date: current.readOn })}
      </p>
    );
  }

  const given = score.trim() !== "";
  const value = Number(score);
  const sound =
    !given ||
    (Number.isFinite(value) &&
      value >= 0 &&
      value <= scale.max &&
      (scale.hasDecimals || Number.isInteger(value)));
  const changed = score.trim() !== (current === null ? "" : String(current.score));

  return (
    <form
      className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || !sound || !changed) return;
        setBusy(true);
        setError(null);
        // Empty clears today's reading rather than recording a nought out of
        // twenty, which is a judgement somebody would have had to make.
        void setOwnRating(cellarId, wineId, given ? value : null)
          .then(onSaved)
          .catch(() => setError(t("wine.ratingFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <label className="text-muted text-sm" htmlFor={fieldId}>
        {t("wine.ownRating", { max: scale.max })}
      </label>
      {/* The width is on a wrapper and not on the field, because `control`
          already carries `w-full`: two width utilities on one element are of
          equal specificity, and which of them wins is decided by the order
          Tailwind happens to emit them in. A box of the size we want, with a
          field that fills it, cannot be overruled by a stylesheet's ordering. */}
      <div className="w-20">
        <input
          id={fieldId}
          inputMode={scale.hasDecimals ? "decimal" : "numeric"}
          value={score}
          onChange={(event) => setScore(event.target.value)}
          className={`${control} ${resting} font-display text-center text-lg`}
        />
      </div>
      {changed && (
        <button type="submit" disabled={busy || !sound} className={actionButton}>
          {busy ? t("wine.saving") : t("wine.saveRating")}
        </button>
      )}
      {/* The date the opinion was formed, which is what makes an old score
          readable as an old score rather than as today's. */}
      {current !== null && !changed && (
        <span className="text-muted text-sm">
          {t("wine.ratedOn", { date: current.readOn })}
        </span>
      )}
      {error !== null && (
        <span role="alert" className="text-past text-sm">
          {error}
        </span>
      )}
    </form>
  );
}
